import { pb } from '../pocketbase/Pocketbase'

// ============================================================================
// Types
// ============================================================================

export interface IReview {
  id: string
  collectionId: string
  collectionName: string
  created: string
  updated: string
  fragrance: string
  user: string
  rating: number
  text: string
}

export interface IReviewExpanded extends IReview {
  expand?: {
    user?: {
      id: string
      username: string
      avatar?: string
    }
    fragrance?: {
      id: string
      name: string
      slug: string
    }
  }
}

export interface IReviewWithUser {
  id: string
  userId: string
  username: string
  avatar: string | null
  rating: number
  text: string
  created: string
}

export interface IReviewWithFragrance {
  id: string
  fragranceId: string
  fragranceName: string
  fragranceSlug: string
  fragranceImage: string | null
  rating: number
  text: string
  created: string
}

export interface IReviewReply {
  id: string
  review: string
  user: string
  text: string
  created: string
}

export interface IReviewReplyWithUser {
  id: string
  userId: string
  username: string
  avatar: string | null
  text: string
  created: string
}

// ============================================================================
// Reviews API
// ============================================================================

/**
 * Get all reviews for a specific fragrance
 */
export async function getReviewsByFragrance(
  fragranceId: string,
  page: number = 1,
  perPage: number = 10
): Promise<{ items: IReviewWithUser[]; totalItems: number; totalPages: number }> {
  const result = await pb.collection('reviews').getList<IReviewExpanded>(page, perPage, {
    filter: `fragrance = "${fragranceId}"`,
    sort: '-created',
    expand: 'user',
  })

  const items: IReviewWithUser[] = result.items.map((review) => ({
    id: review.id,
    userId: review.user,
    username: review.expand?.user?.username || 'Anonymous',
    avatar: review.expand?.user?.avatar
      ? pb.files.getURL(review.expand.user, review.expand.user.avatar)
      : null,
    rating: review.rating,
    text: review.text,
    created: review.created,
  }))

  return {
    items,
    totalItems: result.totalItems,
    totalPages: result.totalPages,
  }
}

/**
 * Get a single review by ID
 */
export async function getReviewById(reviewId: string): Promise<IReviewExpanded> {
  return await pb.collection('reviews').getOne<IReviewExpanded>(reviewId, {
    expand: 'user',
  })
}

/**
 * Create a new review
 */
export async function createReview(
  fragranceId: string,
  rating: number,
  text: string
): Promise<IReview> {
  const userId = pb.authStore.record?.id
  if (!userId) {
    throw new Error('User must be logged in to create a review')
  }

  return await pb.collection('reviews').create<IReview>({
    fragrance: fragranceId,
    rating,
    text
  })
}

/**
 * Update an existing review
 */
export async function updateReview(
  reviewId: string,
  rating: number,
  text: string
): Promise<IReview> {
  return await pb.collection('reviews').update<IReview>(reviewId, {
    rating,
    text,
  })
}

/**
 * Delete a review
 */
export async function deleteReview(reviewId: string): Promise<boolean> {
  return await pb.collection('reviews').delete(reviewId)
}

/**
 * Toggle like on a review - adds like if not liked, removes if already liked
 * Returns the new liked state (likes_count is managed locally by the caller)
 */
export async function toggleReviewLike(reviewId: string, userProfileId: string): Promise<{ liked: boolean }> {
  const userId = pb.authStore.record?.id
  if (!userId) {
    throw new Error('User must be logged in to like a review')
  }

  if (!userProfileId) {
    throw new Error('User profile ID is required')
  }

  try {
    // Check if user already liked this review
    const existingLike = await pb.collection('review_likes').getFirstListItem(
      `review = "${reviewId}" && user = "${userProfileId}"`
    )

    // User already liked - remove the like
    await pb.collection('review_likes').delete(existingLike.id)

    return { liked: false }
  } catch {
    // User hasn't liked yet - add the like
    await pb.collection('review_likes').create({
      review: reviewId,
    })

    return { liked: true }
  }
}

/**
 * Get all liked review IDs for the current user
 */
export async function getUserLikedReviews(userProfileId: string): Promise<Set<string>> {
  const userId = pb.authStore.record?.id
  if (!userId) {
    throw new Error('User must be logged in')
  }

  if (!userProfileId) {
    throw new Error('User profile ID is required')
  }

  try {
    const likes = await pb.collection('review_likes').getFullList({
      filter: `user = "${userProfileId}"`,
      fields: 'review',
    })
    return new Set(likes.map(like => like.review))
  } catch {
    return new Set()
  }
}

/**
 * Get likes count for multiple reviews by counting review_likes records
 */
export async function getReviewLikesCounts(reviewIds: string[]): Promise<Map<string, number>> {
  if (reviewIds.length === 0) return new Map()

  try {
    const filter = reviewIds.map(id => `review = "${id}"`).join(' || ')
    const likes = await pb.collection('review_likes').getFullList({
      filter,
      fields: 'review',
    })

    // Count likes per review
    const counts = new Map<string, number>()
    for (const id of reviewIds) {
      counts.set(id, 0)
    }
    for (const like of likes) {
      const current = counts.get(like.review) || 0
      counts.set(like.review, current + 1)
    }
    return counts
  } catch {
    return new Map()
  }
}

/**
 * Get like status for specific reviews (for the current user)
 */
export async function getUserLikesForReviews(reviewIds: string[], userProfileId: string): Promise<Set<string>> {
  const userId = pb.authStore.record?.id
  if (!userId) {
    throw new Error('User must be logged in')
  }

  if (!userProfileId) {
    throw new Error('User profile ID is required')
  }

  if (reviewIds.length === 0) return new Set()

  try {
    const filter = reviewIds.map(id => `review = "${id}"`).join(' || ')
    const likes = await pb.collection('review_likes').getFullList({
      filter: `user = "${userProfileId}" && (${filter})`,
      fields: 'review',
    })
    return new Set(likes.map(like => like.review))
  } catch {
    return new Set()
  }
}

/**
 * Check if the current user has already reviewed a fragrance
 */
export async function getUserReviewForFragrance(
  fragranceId: string,
  userProfileId: string
): Promise<IReview | null> {
  const userId = pb.authStore.record?.id
  if (!userId) {
    throw new Error('User must be logged in')
  }

  if (!userProfileId) {
    throw new Error('User profile ID is required')
  }

  try {
    const result = await pb.collection('reviews').getFirstListItem<IReview>(
      `fragrance = "${fragranceId}" && user = "${userProfileId}"`
    )
    return result
  } catch (e){
    // No review found
    return null
  }
}

/**
 * Get the average rating for a fragrance
 */
export async function getFragranceAverageRating(
  fragranceId: string
): Promise<{ average: number; count: number }> {
  const result = await pb.collection('reviews').getList<IReview>(1, 1, {
    filter: `fragrance = "${fragranceId}"`,
  })

  if (result.totalItems === 0) {
    return { average: 0, count: 0 }
  }

  // Fetch all reviews to calculate average (only fetch rating field)
  const allReviews = await pb.collection('reviews').getFullList<{ rating: number }>({
    filter: `fragrance = "${fragranceId}"`,
    fields: 'rating',
  })

  const sum = allReviews.reduce((acc, review) => acc + review.rating, 0)
  const average = sum / allReviews.length

  return {
    average: Math.round(average * 10) / 10, // Round to 1 decimal place
    count: allReviews.length,
  }
}

/**
 * Get all reviews by the current user with fragrance details
 */
export async function getCurrentUserReviews(
  page: number = 1,
  perPage: number = 50,
  userProfileId: string
): Promise<{ items: IReviewWithFragrance[]; totalItems: number; totalPages: number }> {
  const userId = pb.authStore.record?.id
  if (!userId) {
    throw new Error('User must be logged in')
  }

  if (!userProfileId) {
    throw new Error('User profile ID is required')
  }

  interface IReviewWithFragranceExpanded extends IReview {
    expand?: {
      fragrance?: {
        id: string
        name: string
        slug: string
        image: string
        collectionId: string
      }
    }
  }

  const result = await pb.collection('reviews').getList<IReviewWithFragranceExpanded>(page, perPage, {
    filter: `user = "${userProfileId}"`,
    sort: '-created',
    expand: 'fragrance',
  })

  const items: IReviewWithFragrance[] = result.items.map((review) => {
    const fragrance = review.expand?.fragrance
    let fragranceImage: string | null = null
    if (fragrance?.image) {
      fragranceImage = pb.files.getURL(fragrance, fragrance.image)
    }

    return {
      id: review.id,
      fragranceId: fragrance?.id || '',
      fragranceName: fragrance?.name || 'Unknown Fragrance',
      fragranceSlug: fragrance?.slug || '',
      fragranceImage,
      rating: review.rating,
      text: review.text,
      created: review.created,
    }
  })

  return {
    items,
    totalItems: result.totalItems,
    totalPages: result.totalPages,
  }
}

// ============================================================================
// Review Replies API
// ============================================================================

/**
 * Create a reply to a review
 */
export async function createReviewReply(
  reviewId: string,
  text: string
): Promise<IReviewReply> {
  const userId = pb.authStore.record?.id
  if (!userId) {
    throw new Error('User must be logged in to reply to a review')
  }

  return await pb.collection('review_replies').create<IReviewReply>({
    review: reviewId,
    text,
  })
}

/**
 * Get all replies for a specific review
 */
export async function getRepliesByReview(
  reviewId: string
): Promise<IReviewReplyWithUser[]> {
  interface IReviewReplyExpanded extends IReviewReply {
    expand?: {
      user?: {
        id: string
        username: string
        avatar?: string
      }
    }
  }

  const result = await pb.collection('review_replies').getFullList<IReviewReplyExpanded>({
    filter: `review = "${reviewId}"`,
    sort: 'created',
    expand: 'user',
  })

  return result.map((reply) => ({
    id: reply.id,
    userId: reply.user,
    username: reply.expand?.user?.username || 'Anonymous',
    avatar: reply.expand?.user?.avatar
      ? pb.files.getURL(reply.expand.user, reply.expand.user.avatar)
      : null,
    text: reply.text,
    created: reply.created,
  }))
}

/**
 * Delete a reply (only the author can delete)
 */
export async function deleteReviewReply(replyId: string): Promise<boolean> {
  return await pb.collection('review_replies').delete(replyId)
}

// ============================================================================
// Review Reports
// ============================================================================

export interface IReviewReport {
  id: string
  review: string
  user: string
  reason: string
  created: string
}

/**
 * Report a review
 */
export async function reportReview(reviewId: string, reason: string, userProfileId: string): Promise<IReviewReport> {
  const userId = pb.authStore.record?.id
  if (!userId) {
    throw new Error('Must be logged in to report a review')
  }

  if (!userProfileId) {
    throw new Error('User profile ID is required')
  }

  return await pb.collection('review_reports').create<IReviewReport>({
    review: reviewId,
    user: userProfileId,
    reason,
  })
}

/**
 * Check if user has already reported a review
 */
export async function hasUserReportedReview(reviewId: string, userProfileId: string): Promise<boolean> {
  const userId = pb.authStore.record?.id
  if (!userId) {
    throw new Error('Must be logged in')
  }

  if (!userProfileId) {
    throw new Error('User profile ID is required')
  }

  try {
    const result = await pb.collection('review_reports').getList(1, 1, {
      filter: pb.filter('review = {:reviewId} && user = {:userProfileId}', { reviewId, userProfileId }),
    })
    return result.totalItems > 0
  } catch {
    return false
  }
}

// ============================================================================
// Reply Reports
// ============================================================================

export interface IReplyReport {
  id: string
  reply: string
  user: string
  reason: string
  created: string
}

/**
 * Report a reply
 */
export async function reportReply(replyId: string, reason: string, userProfileId: string): Promise<IReplyReport> {
  const userId = pb.authStore.record?.id
  if (!userId) {
    throw new Error('Must be logged in to report a reply')
  }

  if (!userProfileId) {
    throw new Error('User profile ID is required')
  }

  return await pb.collection('review_reply_reports').create<IReplyReport>({
    reply: replyId,
    user: userProfileId,
    reason,
  })
}

/**
 * Check if user has already reported a reply
 */
export async function hasUserReportedReply(replyId: string, userProfileId: string): Promise<boolean> {
  const userId = pb.authStore.record?.id
  if (!userId) {
    throw new Error('Must be logged in')
  }

  if (!userProfileId) {
    throw new Error('User profile ID is required')
  }

  try {
    const result = await pb.collection('review_reply_reports').getList(1, 1, {
      filter: pb.filter('reply = {:replyId} && user = {:userProfileId}', { replyId, userProfileId }),
    })
    return result.totalItems > 0
  } catch {
    return false
  }
}

// ============================================================================
// Review Reports Admin API
// ============================================================================

export interface IReviewReportExpanded extends IReviewReport {
  expand?: {
    user?: {
      id: string
      username: string
      avatar?: string
    }
    review?: {
      id: string
      text: string
      rating: number
      user: string
      fragrance: string
      expand?: {
        user?: {
          id: string
          username: string
        }
        fragrance?: {
          id: string
          name: string
          slug: string
        }
      }
    }
  }
}

export interface IReviewReportWithDetails {
  id: string
  reason: string
  created: string
  // Reporter info
  reporterId: string
  reporterUsername: string
  // Review info
  reviewId: string
  reviewText: string
  reviewRating: number
  // Review author info
  reviewAuthorId: string
  reviewAuthorUsername: string
  // Fragrance info
  fragranceId: string
  fragranceSlug: string
  fragranceName: string
}

/**
 * Get all review reports with expanded user and review details (admin only)
 */
export async function getReviewReports(
  page: number = 1,
  perPage: number = 10
): Promise<{ items: IReviewReportWithDetails[]; totalItems: number; totalPages: number }> {
  const result = await pb.collection('review_reports').getList<IReviewReportExpanded>(page, perPage, {
    sort: '-created',
    expand: 'user,review,review.user,review.fragrance',
  })

  const items: IReviewReportWithDetails[] = result.items.map((report) => ({
    id: report.id,
    reason: report.reason,
    created: report.created,
    reporterId: report.user,
    reporterUsername: report.expand?.user?.username || 'Unknown',
    reviewId: report.review,
    reviewText: report.expand?.review?.text || '',
    reviewRating: report.expand?.review?.rating || 0,
    reviewAuthorId: report.expand?.review?.user || '',
    reviewAuthorUsername: report.expand?.review?.expand?.user?.username || 'Unknown',
    fragranceId: report.expand?.review?.expand?.fragrance?.id || '',
    fragranceSlug: report.expand?.review?.expand?.fragrance?.slug || '',
    fragranceName: report.expand?.review?.expand?.fragrance?.name || 'Unknown',
  }))

  return {
    items,
    totalItems: result.totalItems,
    totalPages: result.totalPages,
  }
}

/**
 * Ignore/dismiss a report (delete the report but keep the review)
 */
export async function ignoreReviewReport(reportId: string): Promise<boolean> {
  return await pb.collection('review_reports').delete(reportId)
}

/**
 * Delete a report and the associated review
 */
export async function deleteReviewAndReport(reportId: string, reviewId: string): Promise<boolean> {
  // Delete the review first (this will cascade delete related reports if configured)
  await pb.collection('reviews').delete(reviewId)
  // Then delete the report if it still exists
  try {
    await pb.collection('review_reports').delete(reportId)
  } catch {
    // Report may have been cascade deleted
  }
  return true
}

// ============================================================================
// Reply Reports Admin API
// ============================================================================

export interface IReplyReportExpanded extends IReplyReport {
  expand?: {
    user?: {
      id: string
      username: string
      avatar?: string
    }
    reply?: {
      id: string
      text: string
      user: string
      review: string
      expand?: {
        user?: {
          id: string
          username: string
        }
        review?: {
          id: string
          fragrance: string
          expand?: {
            fragrance?: {
              id: string
              name: string
              slug: string
            }
          }
        }
      }
    }
  }
}

export interface IReplyReportWithDetails {
  id: string
  reason: string
  created: string
  // Reporter info
  reporterId: string
  reporterUsername: string
  // Reply info
  replyId: string
  replyText: string
  // Reply author info
  replyAuthorId: string
  replyAuthorUsername: string
  // Review info (the review the reply belongs to)
  reviewId: string
  // Fragrance info (from the review the reply belongs to)
  fragranceId: string
  fragranceSlug: string
  fragranceName: string
}

/**
 * Get all reply reports with expanded user and reply details (admin only)
 */
export async function getReplyReports(
  page: number = 1,
  perPage: number = 10
): Promise<{ items: IReplyReportWithDetails[]; totalItems: number; totalPages: number }> {
  const result = await pb.collection('review_reply_reports').getList<IReplyReportExpanded>(page, perPage, {
    sort: '-created',
    expand: 'user,reply,reply.user,reply.review,reply.review.fragrance',
  })

  const items: IReplyReportWithDetails[] = result.items.map((report) => ({
    id: report.id,
    reason: report.reason,
    created: report.created,
    reporterId: report.user,
    reporterUsername: report.expand?.user?.username || 'Unknown',
    replyId: report.reply,
    replyText: report.expand?.reply?.text || '',
    replyAuthorId: report.expand?.reply?.user || '',
    replyAuthorUsername: report.expand?.reply?.expand?.user?.username || 'Unknown',
    reviewId: report.expand?.reply?.review || '',
    fragranceId: report.expand?.reply?.expand?.review?.expand?.fragrance?.id || '',
    fragranceSlug: report.expand?.reply?.expand?.review?.expand?.fragrance?.slug || '',
    fragranceName: report.expand?.reply?.expand?.review?.expand?.fragrance?.name || 'Unknown',
  }))

  return {
    items,
    totalItems: result.totalItems,
    totalPages: result.totalPages,
  }
}

/**
 * Ignore/dismiss a reply report (delete the report but keep the reply)
 */
export async function ignoreReplyReport(reportId: string): Promise<boolean> {
  return await pb.collection('review_reply_reports').delete(reportId)
}

/**
 * Delete a reply report and the associated reply
 */
export async function deleteReplyAndReport(reportId: string, replyId: string): Promise<boolean> {
  // Delete the reply first
  await pb.collection('review_replies').delete(replyId)
  // Then delete the report if it still exists
  try {
    await pb.collection('review_reply_reports').delete(reportId)
  } catch {
    // Report may have been cascade deleted
  }
  return true
}
